import React, { useState, useEffect } from 'react';
import { List, Rate, Typography, Avatar, Tag, Empty, Spin } from 'antd';
import { UserOutlined, CheckCircleOutlined } from '@ant-design/icons';
import reviewService from '../../services/reviewService';
import './ReviewList.scss';

const { Text, Paragraph } = Typography;

/**
 * ReviewList - displays reviews for products or services
 * @param {string} type - 'product' or 'service'
 * @param {number} itemId - productId or serviceId
 */
const ReviewList = ({ type = 'product', itemId }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ average_rating: 0, rating_count: 0 });

  useEffect(() => {
    fetchReviews();
  }, [itemId, type]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const response = type === 'product'
        ? await reviewService.getProductReviews(itemId)
        : await reviewService.getServiceReviews(itemId);
      setReviews(response.data.reviews || []);
      setStats({
        average_rating: response.data.average_rating || 0,
        rating_count: response.data.rating_count || 0,
      });
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="review-list">
      <div className="review-summary">
        <div className="rating-display">
          <span className="rating-number">{stats.average_rating.toFixed(1)}</span>
          <div className="rating-stars">
            <Rate disabled allowHalf value={stats.average_rating} />
            <Text type="secondary">({stats.rating_count} reviews)</Text>
          </div>
        </div>
      </div>

      {reviews.length === 0 ? (
        <Empty
          description="No reviews yet. Be the first to review this product!"
          style={{ padding: '40px 0' }}
        />
      ) : (
        <List
          itemLayout="vertical"
          dataSource={reviews}
          renderItem={(review) => (
            <List.Item className="review-item">
              <div className="review-header">
                <Avatar
                  style={{ backgroundColor: '#9FB18C' }}
                  icon={<UserOutlined />}
                >
                  {review.user_initial}
                </Avatar>
                <div className="review-meta">
                  <div className="reviewer-info">
                    <Text strong>{review.user_name}</Text>
                    {(review.is_verified_purchase || review.is_verified) && (
                      <Tag color="green" icon={<CheckCircleOutlined />} style={{ marginLeft: 8 }}>
                        {type === 'product' ? 'Verified Purchase' : 'Verified Appointment'}
                      </Tag>
                    )}
                  </div>
                  <div className="review-rating-date">
                    <Rate disabled value={review.rating} style={{ fontSize: 14 }} />
                    <Text type="secondary" style={{ marginLeft: 12 }}>
                      {formatDate(review.created_at)}
                    </Text>
                  </div>
                </div>
              </div>
              {review.comment && (
                <Paragraph className="review-comment">
                  {review.comment}
                </Paragraph>
              )}
            </List.Item>
          )}
        />
      )}
    </div>
  );
};

export default ReviewList;
